import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Coins, Sparkles, Zap, Crown, ArrowLeft, Star, Check } from "lucide-react";
import { toast } from "sonner";

const PACK_ICONS = {
  starter: Zap,
  fan: Star,
  superfan: Sparkles,
  vip: Crown,
};

const PACK_COLORS = {
  starter: "from-blue-500 to-cyan-400",
  fan: "from-[#FF6B35] to-[#FF8FA3]",
  superfan: "from-[#7000FF] to-[#FF6B35]",
  vip: "from-yellow-400 to-orange-500",
};

export default function CreditsShopPage() {
  const { api, user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [packs, setPacks] = useState({});
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);

  const fetchPacks = useCallback(async () => {
    try {
      const res = await api.get("/payments/packs");
      setPacks(res.data.packs);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchPacks();
  }, [fetchPacks]);

  const handleBuy = async (packId) => {
    setBuying(packId);
    try {
      const res = await api.post("/payments/checkout", {
        pack_id: packId,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de paiement");
      setBuying(null);
    }
  };

  const packOrder = ["starter", "fan", "superfan", "vip"];

  return (
    <div className="py-6 space-y-6" data-testid="credits-shop-page">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-[#AFA8BA] transition-colors"
        data-testid="shop-back-btn"
      >
        <ArrowLeft className="w-4 h-4" />
        Retour
      </button>

      <div>
        <h1 className="font-heading font-bold text-2xl text-foreground mb-1">Boutique de credits</h1>
        <p className="text-sm text-muted-foreground">Achete des credits pour miser sur tes artistes preferes</p>
      </div>

      {/* Current balance */}
      <div className="bg-card border border-border rounded-3xl p-5 flex items-center gap-4 shadow-sm">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FF6B35] to-[#FF8FA3] flex items-center justify-center">
          <Coins className="w-7 h-7 text-white" />
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Ton solde actuel</p>
          <p className="font-mono-credits font-bold text-2xl text-card-foreground" data-testid="shop-balance">
            {user?.credits_balance || 0} credits
          </p>
        </div>
      </div>

      {/* Packs */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-card rounded-3xl h-32 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {packOrder.map((packId) => {
            const pack = packs[packId];
            if (!pack) return null;
            const Icon = PACK_ICONS[packId] || Coins;
            const colors = PACK_COLORS[packId] || "from-gray-500 to-gray-400";
            const isBuying = buying === packId;
            const isPopular = packId === "superfan";

            return (
              <div
                key={packId}
                className={`relative bg-card border rounded-3xl p-5 shadow-sm transition-all duration-200 hover:scale-[1.01] ${
                  isPopular ? "border-[#FF6B35] ring-1 ring-[#FF6B35]/20" : "border-border"
                }`}
                data-testid={`pack-${packId}`}
              >
                {isPopular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] px-3 py-0.5 rounded-full bg-[#FF6B35] text-white font-bold">
                    Populaire
                  </span>
                )}

                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${colors} flex items-center justify-center shrink-0`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-heading font-bold text-base text-card-foreground">{pack.label}</h3>
                      {pack.bonus && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200 font-medium">
                          {pack.bonus}
                        </span>
                      )}
                    </div>
                    <p className="font-mono-credits text-lg font-bold text-[#FF6B35]">
                      {pack.credits.toLocaleString()} credits
                    </p>
                  </div>

                  <button
                    onClick={() => handleBuy(packId)}
                    disabled={isBuying}
                    className={`font-bold px-5 py-2.5 rounded-full text-sm transition-all duration-200 shrink-0 ${
                      isPopular
                        ? "bg-[#FF6B35] text-white shadow-[0_0_15px_rgba(255,107,53,0.3)] hover:scale-105 active:scale-95"
                        : "bg-muted text-card-foreground border border-border hover:bg-border"
                    } disabled:opacity-50`}
                    data-testid={`buy-${packId}`}
                  >
                    {isBuying ? "..." : `${pack.price.toFixed(2)} EUR`}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Info */}
      <div className="bg-card border border-border rounded-2xl p-4 space-y-2 shadow-sm">
        <h4 className="font-heading font-bold text-xs text-card-foreground">A quoi servent les credits ?</h4>
        <div className="space-y-1.5">
          {[
            "Mise sur tes artistes preferes pour booster leur cote",
            "Utilise tes credits pour encherir sur des lots exclusifs",
            "Gagne des credits gratuits chaque jour avec le Daily Streak",
          ].map((text, i) => (
            <div key={i} className="flex items-start gap-2">
              <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
              <p className="text-xs text-card-foreground/80">{text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
